import { create } from "@bufbuild/protobuf";
import { ArrowLeftIcon, ImageBrokenIcon } from "@phosphor-icons/react";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

import Button, { ButtonSize, ButtonVariant } from "@gx/dls/buttons/Button";
import { GetProjectRequestSchema } from "@gx/ts-contracts/services/ontology/api/project_pb";

import ErrorLayout from "@/layouts/ErrorLayout";
import FullScreenLoader from "@/layouts/FullScreenLoader";
import ProjectLayout from "@/layouts/project/ProjectLayout";

import ProjectPageEntities from "@/pages/projects/components/project/ProjectPageObjects";

import { useGetProjectQuery } from "@/api/queries/projects";

const searchSchema = z.object({
  addProjectSources: z.coerce.boolean().optional(),
  nodeId: z.string().optional(),
  edgeId: z.string().optional(),
  search: z.string().optional(),
});

export const Route = createFileRoute("/_secure/projects/$projectId/objects/")({
  validateSearch: zodValidator(searchSchema),
  component: RouteComponent,
});

function RouteComponent() {
  const { projectId } = useParams({ from: "/_secure/projects/$projectId/objects/" });
  const navigate = useNavigate();

  const { data: project, isLoading: isLoadingProject } = useGetProjectQuery({
    input: create(GetProjectRequestSchema, { projectId }),
  });

  const handleGoToDashboard = () => {
    void navigate({ to: "/projects", search: {} });
  };

  if (isLoadingProject) {
    return <FullScreenLoader />;
  }

  if (!project?.project) {
    return (
      <ErrorLayout
        icon={ImageBrokenIcon}
        header="Project not found"
        message="The project you are looking for does not exist"
        actions={
          <Button
            label="Go to dashboard"
            icon={ArrowLeftIcon}
            variant={ButtonVariant.SECONDARY}
            size={ButtonSize.SMALL}
            onClick={handleGoToDashboard}
          />
        }
      />
    );
  }

  return (
    <ProjectLayout project={project.project}>
      <ProjectPageEntities project={project.project} />
    </ProjectLayout>
  );
}
